import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, Divider, Grid } from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InlineIconButton from "../../Buttons/InlineIconButton/InlineIconButton";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import CityAutocomplete from "../../Inputs/CityAutocomplete/CityAutocomplete";
import CarrierAutocomplete from "../../Inputs/CarrierAutocomplete/CarrierAutocomplete";
import CarrierDotNumberAutocomplete from "../../Inputs/CarrierDotNumberAutocomplete/CarrierDotNumberAutocomplete";
import CarrierMcNumberAutocomplete from "../../Inputs/CarrierMcNumberSearch/CarrierMcNumberAutocomplete";
import CarrierIdAutocomplete from "../../Inputs/CarrierIdAutocomplete/CarrierIdAutocomplete";
import CustomerAutocomplete from "../../Inputs/CustomerAutocomplete/CustomerAutocomplete";
import PrimaryAutocompleteSelect from "../../Inputs/PrimaryAutocompleteSelect/PrimaryAutocompleteSelect";
import CarrierSearchCreateModal from "../CarrierSearchCreateModal/CarrierSearchCreateModal";
import useLoadStatus from "../../../hooks/useLoadStatus/useLoadStatus";
import useCarrierTranslations from "../../../hooks/useCarrierTranslations/useCarrierTranslations";
import useEquipmentTypes from "../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { CarrierService } from "../../../services/CarrierService";
import { SecurityService } from "../../../services/SecurityService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function CarrierSearchFilters({
  isLoading,
  filters,
  setFilters,
  selectedSearch,
  handleSave,
  handleSearch,
  handleUpdate
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const { loadStatuses } = useLoadStatus();
  const { modesOptions } = useEquipmentTypes();
  const { safetyRatingsOptions, certificationOptions, trailerAccessoriesOptions, cargoTypesOptions } = useCarrierTranslations();
  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState([]);
  const [dispatchersOptions, setDispatchersOptions] = useState([]);
  const [managersOptions, setManagersOptions] = useState([]);
  const [initOriginLocation, setInitOriginLocation] = useState("");
  const [initOriginInput, setInitOriginInput] = useState("");
  const [originLocation, setOriginLocation] = useState({});
  const [initOriginType, setInitOriginType] = useState("");
  const [originType, setOriginType] = useState("");
  const [originRadius, setOriginRadius] = useState("50");
  const [initDestLocation, setInitDestLocation] = useState("");
  const [initDestInput, setInitDestInput] = useState("");
  const [destLocation, setDestLocation] = useState({});
  const [initDestType, setInitDestType] = useState("");
  const [destType, setDestType] = useState("");
  const [destRadius, setDestRadius] = useState("50");
  const [carrierId, setCarrierId] = useState("");
  const [carrier, setCarrier] = useState(null);
  const [initHomeLocation, setInitHomeLocation] = useState("");
  const [initHomeInput, setInitHomeInput] = useState("");
  const [homeLocation, setHomeLocation] = useState({});
  const [initHomeType, setInitHomeType] = useState("");
  const [homeType, setHomeType] = useState("");
  const [homeLocationRadius, setHomeLocationRadius] = useState("50");
  const [modes, setModes] = useState(modesOptions);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [brokerageStatuses, setBrokerageStatuses] = useState(loadStatuses.map(({ id }) => id));
  const [certifications, setCertifications] = useState(certificationOptions.map(({ value }) => value));
  const [trailerAccessories, setTrailerAccessories] = useState(trailerAccessoriesOptions.map(({ value }) => value));
  const [dispatchers, setDispatchers] = useState([]);
  const [carrierStatuses, setCarrierStatuses] = useState(["1", "0"]);
  const [pipelineStatuses, setPipelineStatuses] = useState(["OPEN", "LEAD", "RELATN"]);
  const [safetyRatings, setSafetyRatings] = useState(safetyRatingsOptions.map(({ value }) => value));
  const [managers, setManagers] = useState([]);
  const [cargoTypes, setCargoTypes] = useState(cargoTypesOptions.map(({ value }) => value));
  const [customers, setCustomers] = useState([]);
  const [cargoInsurances, setCargoInsurances] = useState(["0 100000", "100000 250000", "250000 500000", "500000 1000000", "1000000 100000000000"]);
  const [truckUnits, setTruckUnits] = useState(["1 25", "26 50", "51 100", "101 1000000000"]);
  const [lookBackDays, setLookBackDays] = useState("90");

  const statusOptions = useMemo(() => [
    {
      name: "Active",
      value: "1"
    },
    {
      name: "Inactive",
      value: "0"
    }
  ], []);

  const pipelineStatusOptions = useMemo(() => [
    {
      name: "Open",
      value: "OPEN"
    },
    {
      name: "Lead",
      value: "LEAD"
    },
    {
      name: "Relationship",
      value: "RELATN"
    }
  ], []);

  const cargoInsuranceOptions = useMemo(() => [
    {
      name: "Up to $100,000",
      value: "0 100000"
    },
    {
      name: "$100,000 to $250,000",
      value: "100000 250000"
    },
    {
      name: "$250,000 to $500,000",
      value: "250000 500000"
    },
    {
      name: "$500,000 to $1,000,000",
      value: "500000 1000000"
    },
    {
      name: "$1,000,000+",
      value: "1000000 100000000000"
    }
  ], []);

  const truckUnitsOptions = useMemo(() => [
    {
      name: "1 - 25",
      value: "1 25",
    },
    {
      name: "26 - 50",
      value: "26 50"
    },
    {
      name: "51 - 100",
      value: "51 100"
    },
    {
      name: "100+",
      value: "101 1000000000"
    }
  ], []);

  const lookBackDayOptions = useMemo(() => [
    {
      name: "Past 30 days",
      value: 30
    },
    {
      name: "Past 60 days",
      value: 60
    },
    {
      name: "Past 90 days",
      value: 90
    },
    {
      name: "Past 6 months",
      value: 180
    },
    {
      name: "Past 1 Year",
      value: 365
    },
    {
      name: "Past 2 Years",
      value: 730
    },
    {
      name: "Past 3 Years",
      value: 1095
    },
  ], []);

  const handleReset = useCallback(() => {
    setInitOriginLocation("");
    setInitOriginInput("");
    setOriginLocation({});
    setInitOriginType("");
    setOriginType("");
    setOriginRadius("50");
    setInitDestLocation("");
    setInitDestInput("");
    setDestLocation({});
    setInitDestType("");
    setDestType("");
    setDestRadius("50");
    setCarrierId("");
    setCarrier(null);
    setInitHomeLocation("");
    setInitHomeInput("");
    setHomeLocation({});
    setInitHomeType("");
    setHomeType("");
    setHomeLocationRadius("50");
    setModes(modesOptions);
    setEquipmentTypes([]);
    setBrokerageStatuses(loadStatuses.map(({ id }) => id));
    setCertifications(certificationOptions.map(({ value }) => value));
    setTrailerAccessories(trailerAccessoriesOptions.map(({ value }) => value));
    setDispatchers([]);
    setCarrierStatuses(["1", "0"]);
    setPipelineStatuses(["OPEN", "LEAD", "RELATN"]);
    setSafetyRatings(safetyRatingsOptions.map(({ value }) => value));
    setManagers([]);
    setCargoTypes(cargoTypesOptions.map(({ value }) => value));
    setCustomers([]);
    setCargoInsurances(["0 100000", "100000 250000", "250000 500000", "500000 1000000", "1000000 100000000000"]);
    setTruckUnits(["1 25", "26 50", "51 100", "101 1000000000"]);
    setLookBackDays("90");
  }, [
    modesOptions,
    loadStatuses,
    certificationOptions,
    trailerAccessoriesOptions,
    safetyRatingsOptions,
    cargoTypesOptions
  ]);

  const handleSwitch = useCallback(() => {
    setOriginLocation(destLocation);
    setInitOriginLocation(destLocation);
    setOriginType(destType);
    setInitOriginType(destType);
    setOriginRadius(destRadius);
    setDestLocation(originLocation);
    setInitDestLocation(originLocation);
    setDestType(originType);
    setInitDestType(originType);
    setDestRadius(originRadius);
  }, [
    destLocation,
    destType,
    destRadius,
    originLocation,
    originType,
    originRadius
  ]);

  useEffect(() => {
    const customFilters = [
      { fieldName: "numberOfRows", params: "500", isQuery: true },
      { fieldName: "lookBackDay", params: `${lookBackDays}`, isQuery: true }
    ];

    if (originLocation?.id) {
      customFilters.push(
        { fieldName: "originCityState", params: `${originLocation.name} ${originLocation.state}`, isQuery: true },
        { fieldName: "originCityStateString", params: `${originLocation.name}, ${originLocation.state}`, isQuery: false });
    }

    if (originLocation?.id && originRadius) {
      customFilters.push({ fieldName: "searchMiles", params: originRadius, isQuery: true });
    }

    if (destLocation?.id) {
      customFilters.push(
        { fieldName: "destinationCityState", params: `${destLocation.name} ${destLocation.state}`, isQuery: true },
        { fieldName: "destinationCityStateString", params: `${destLocation.name}, ${destLocation.state}`, isQuery: false });
    }

    if (destLocation?.id && destRadius) {
      customFilters.push({ fieldName: "searchDestinationMiles", params: destRadius, isQuery: true });
    }

    if (carrier) {
      customFilters.push({ fieldName: "carrierCode", params: carrier.id, isQuery: true });
    }

    if (homeLocation?.id) {
      customFilters.push(
        { fieldName: "homeCityState", params: `${homeLocation.name} ${homeLocation.state}`, isQuery: true },
        { fieldName: "homeCityStateString", params: `${homeLocation.name}, ${homeLocation.state}`, isQuery: false });
    }

    if (homeLocation?.id && homeLocationRadius) {
      customFilters.push({ fieldName: "searchHomeMiles", params: homeLocationRadius, isQuery: true });
    }

    if (modes.length > 0) {
      customFilters.push({ fieldName: "mode", params: modes.join("|"), isQuery: modes.length < modesOptions.length });
    }

    if (equipmentTypes.length > 0) {
      customFilters.push({ fieldName: "equipmentType", params: equipmentTypes.join("|"), isQuery: equipmentTypes.length < equipmentTypesOptions.length });
    }

    if (brokerageStatuses.length > 0) {
      customFilters.push({ fieldName: "brokerageStatus", params: brokerageStatuses.join("|"), isQuery: brokerageStatuses.length < loadStatuses.length });
    }

    if (certifications.length > 0) {
      customFilters.push({ fieldName: "certification", params: certifications.join("|"), isQuery: certifications.length < certificationOptions.length });
    }

    if (trailerAccessories.length > 0) {
      customFilters.push({ fieldName: "trailerAccessories", params: trailerAccessories.join("|"), isQuery: trailerAccessories.length < trailerAccessoriesOptions.length });
    }

    if (dispatchers.length > 0 && dispatchers.some(dis => Object.keys(dis).includes("dispatcher_User_Id"))) {
      customFilters.push({ fieldName: "dispatchers", params: dispatchers.filter(dis => typeof (dis) === "object").map(({ dispatcher_User_Id }) => dispatcher_User_Id.trim()).join("|"), isQuery: true });
    }

    if (carrierStatuses.length > 0) {
      customFilters.push({ fieldName: "carrierStatus", params: carrierStatuses.join("|"), isQuery: carrierStatuses.length < statusOptions.length });
    }

    if (pipelineStatuses.length > 0) {
      customFilters.push({ fieldName: "pipeLineStatus", params: pipelineStatuses.join("|"), isQuery: pipelineStatuses.length < pipelineStatusOptions.length });
    }

    if (safetyRatings.length > 0) {
      customFilters.push({ fieldName: "safetyRating", params: safetyRatings.join("|"), isQuery: safetyRatings.length < safetyRatingsOptions.length });
    }

    if (managers.length > 0 && managers.some(man => Object.keys(man).includes("carrier_Mgr"))) {
      customFilters.push({ fieldName: "carrierManager", params: managers.filter(man => typeof (man) === "object").map(({ carrier_Mgr }) => carrier_Mgr.trim()).join("|"), isQuery: true });
    }

    if (cargoTypes.length > 0) {
      customFilters.push({ fieldName: "cargoType", params: cargoTypes.join("|"), isQuery: cargoTypes.length < cargoTypesOptions.length });
    }

    if (customers.length > 0) {
      customFilters.push({ fieldName: "customer", params: customers.join("|"), isQuery: true });
    }

    if (cargoInsurances.length > 0) {
      customFilters.push({ fieldName: "cargoInsurance", params: cargoInsurances.join("|"), isQuery: cargoInsurances.length < cargoInsuranceOptions.length });
    }

    if (truckUnits.length > 0) {
      customFilters.push({ fieldName: "truckUnit", params: truckUnits.join("|"), isQuery: truckUnits.length < truckUnitsOptions.length });
    }

    setFilters(customFilters);
  }, [
    originLocation,
    originRadius,
    destLocation,
    destRadius,
    carrier,
    homeLocation,
    homeLocationRadius,
    modes,
    equipmentTypes,
    brokerageStatuses,
    certifications,
    trailerAccessories,
    dispatchers,
    carrierStatuses,
    pipelineStatuses,
    safetyRatings,
    managers,
    cargoTypes,
    customers,
    cargoInsurances,
    truckUnits,
    lookBackDays
  ]);

  useEffect(() => {
    if (filters.length > 0) {
      const customFilters = [
        { fieldName: "numberOfRows", params: "500", isQuery: true }
      ];

      const origin = filters.find(({ fieldName }) => fieldName === "originCityState");
      const originString = filters.find(({ fieldName }) => fieldName === "originCityStateString");
      if (origin && originString) {
        setInitOriginInput(originString.params);
        setOriginType("name");

        originString.isQuery = false;
        customFilters.push(originString);

        origin.isQuery = true;
        customFilters.push(origin);
      } else {
        setInitOriginLocation("");
        setInitOriginInput("");
        setOriginLocation({});
        setInitOriginType("");
        setOriginType("");
      }

      const originRadius = filters.find(({ fieldName }) => fieldName === "searchMiles");
      if (originRadius) {
        setOriginRadius(originRadius.params);

        originRadius.isQuery = true;
        customFilters.push(originRadius);
      } else {
        setOriginRadius("50");
      }

      const destination = filters.find(({ fieldName }) => fieldName === "destinationCityState");
      const destinationString = filters.find(({ fieldName }) => fieldName === "destinationCityStateString");
      if (destination && destinationString) {
        setInitDestInput(destinationString.params);
        setOriginType("name");

        destinationString.isQuery = false;
        customFilters.push(destinationString);

        destination.isQuery = true;
        customFilters.push(destination);
      } else {
        setInitDestLocation("");
        setInitDestInput("");
        setDestLocation({});
        setInitDestType("");
        setDestType("");
      }

      const destRadius = filters.find(({ fieldName }) => fieldName === "searchDestinationMiles");
      if (destRadius) {
        setDestRadius(destRadius.params);

        destRadius.isQuery = true;
        customFilters.push(destRadius);
      } else {
        setDestRadius("50");
      }

      const carrierId = filters.find(({ fieldName }) => fieldName === "carrierCode");
      if (carrierId) {
        setCarrierId(carrierId.params);

        carrierId.isQuery = true;
        customFilters.push(carrierId);
      } else {
        setCarrierId("");
        setCarrier(null);
      }

      const home = filters.find(({ fieldName }) => fieldName === "homeCityState");
      const homeString = filters.find(({ fieldName }) => fieldName === "homeCityStateString");
      if (home && homeString) {
        setInitHomeInput(homeString.params);
        setHomeType("name");

        homeString.isQuery = true;
        customFilters.push(homeString);

        home.isQuery = true;
        customFilters.push(home);
      } else {
        setInitHomeLocation("");
        setInitHomeInput("");
        setHomeLocation({});
        setInitHomeType("");
        setHomeType("");
      }

      const homeLocationRadius = filters.find(({ fieldName }) => fieldName === "searchHomeMiles");
      if (homeLocationRadius) {
        setHomeLocationRadius(homeLocationRadius.params);

        homeLocationRadius.isQuery = true;
        customFilters.push(homeLocationRadius);
      } else {
        setHomeLocationRadius("50");
      }

      const modes = filters.find(({ fieldName }) => fieldName === "mode");
      if (modes) {
        setModes(modes.params.split("|"));

        modes.isQuery = modes.params.split("|").length < modesOptions.length;
        customFilters.push(modes);
      } else {
        setModes([]);
      }

      const equipmentTypes = filters.find(({ fieldName }) => fieldName === "equipmentType");
      if (equipmentTypes) {
        setEquipmentTypes(equipmentTypes.params.split("|"));

        equipmentTypes.isQuery = equipmentTypes.params.split("|").length < equipmentTypesOptions.length;
        customFilters.push(equipmentTypes);
      } else {
        setEquipmentTypes([]);
      }

      const brokerageStatuses = filters.find(({ fieldName }) => fieldName === "brokerageStatus");
      if (brokerageStatuses) {
        setBrokerageStatuses(brokerageStatuses.params.split("|"));

        brokerageStatuses.isQuery = brokerageStatuses.params.split("|").length < loadStatuses.length;
        customFilters.push(brokerageStatuses);
      } else {
        setBrokerageStatuses([]);
      }

      const certifications = filters.find(({ fieldName }) => fieldName === "certification");
      if (certifications) {
        setCertifications(certifications.params.split("|"));

        certifications.isQuery = certifications.params.split("|").length < certificationOptions.length;
        customFilters.push(certifications);
      } else {
        setCertifications([]);
      }

      const trailerAccessories = filters.find(({ fieldName }) => fieldName === "trailerAccessories");
      if (trailerAccessories) {
        setTrailerAccessories(trailerAccessories.params.split("|"));

        trailerAccessories.isQuery = trailerAccessories.params.split("|").length < trailerAccessoriesOptions.length;
        customFilters.push(trailerAccessories);
      } else {
        setTrailerAccessories([]);
      }

      const dispatchers = filters.find(({ fieldName }) => fieldName === "dispatchers");
      if (dispatchers) {
        setDispatchers(dispatchersOptions.filter(({ dispatcher_User_Id }) => dispatchers.params.includes(dispatcher_User_Id.trim())));

        dispatchers.isQuery = true;
        customFilters.push(dispatchers);
      } else {
        setDispatchers([]);
      }

      const carrierStatuses = filters.find(({ fieldName }) => fieldName === "carrierStatus");
      if (carrierStatuses) {
        setCarrierStatuses(carrierStatuses.params.split("|"));

        carrierStatuses.isQuery = carrierStatuses.params.split("|").length < statusOptions.length;
        customFilters.push(carrierStatuses);
      } else {
        setCarrierStatuses([]);
      }

      const pipelineStatuses = filters.find(({ fieldName }) => fieldName === "pipeLineStatus");
      if (pipelineStatuses) {
        setPipelineStatuses(pipelineStatuses.params.split("|"));

        pipelineStatuses.isQuery = pipelineStatuses.params.split("|").length < pipelineStatusOptions.length;
        customFilters.push(pipelineStatuses);
      } else {
        setPipelineStatuses([]);
      }

      const safetyRatings = filters.find(({ fieldName }) => fieldName === "safetyRating");
      if (safetyRatings) {
        setSafetyRatings(safetyRatings.params.split("|"));

        safetyRatings.isQuery = safetyRatings.params.split("|").length < safetyRatingsOptions.length;
        customFilters.push(safetyRatings);
      } else {
        setSafetyRatings([]);
      }

      const carrierManagers = filters.find(({ fieldName }) => fieldName === "carrierManager");
      if (carrierManagers) {
        setManagers(managersOptions.filter(({ carrier_Mgr }) => carrierManagers.params.includes(carrier_Mgr.trim())));

        carrierManagers.isQuery = true;
        customFilters.push(carrierManagers);
      } else {
        setManagers([]);
      }

      const cargoTypes = filters.find(({ fieldName }) => fieldName === "cargoType");
      if (cargoTypes) {
        setCargoTypes(cargoTypes.params.split("|"));

        cargoTypes.isQuery = cargoTypes.params.split("|").length < cargoTypesOptions.length;
        customFilters.push(cargoTypes);
      } else {
        setCargoTypes([]);
      }

      const customers = filters.find(({ fieldName }) => fieldName === "customer");
      if (customers) {
        setCustomers(customers.params.split("|"));

        customers.isQuery = true;
        customFilters.push(customers);
      } else {
        setCustomers([]);
      }

      const cargoInsurances = filters.find(({ fieldName }) => fieldName === "cargoInsurance");
      if (cargoInsurances) {
        setCargoInsurances(cargoInsurances.params.split("|"));

        cargoInsurances.isQuery = cargoInsurances.params.split("|").length < cargoInsuranceOptions.length;
        customFilters.push(cargoInsurances);
      } else {
        setCargoInsurances([]);
      }

      const truckUnits = filters.find(({ fieldName }) => fieldName === "truckUnit");
      if (truckUnits) {
        setTruckUnits(truckUnits.params.split("|"));

        truckUnits.isQuery = truckUnits.params.split("|").length < truckUnits.length;
        customFilters.push(truckUnits);
      } else {
        setTruckUnits([]);
      }

      const lookBackDays = filters.find(({ fieldName }) => fieldName === "lookBackDay");
      if (lookBackDays) {
        setLookBackDays(lookBackDays.params);

        lookBackDays.isQuery = true;
        customFilters.push(lookBackDays);
      } else {
        setLookBackDays(365);
      }

      handleSearch(customFilters);
    } else {
      handleReset();
    }
  }, [selectedSearch]);

  useEffect(() => {
    setBrokerageStatuses(loadStatuses.map(({ id }) => id));
  }, [loadStatuses]);

  useEffect(() => {
    CarrierService.GetCarrierEquipmentTypes(instance).then(res => {
      setEquipmentTypesOptions(res);
      setEquipmentTypes(res.map(({ id }) => id));
    }).catch(error => {
      console.error(error);
      warning(error);
    });

    SecurityService.GetDispatchers(instance).then(res => {
      setDispatchersOptions(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });

    CarrierService.GetCarrierManagers(instance).then(res => {
      setManagersOptions(res);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  }, []);

  return useMemo(() => {
    return (
      <Box sx={{ backgroundColor: "black_opacity.05", padding: "20px 30px" }} >
        <Typography variant="h1" color="black_opacity.100">Carrier Search</Typography>
        <Box>
          <Box marginBottom="10px"><Typography variant="label1" color="black_opacity.80">Lane</Typography></Box>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <CityAutocomplete
                  label="Origin"
                  allowState
                  stateOnly={destType === "state" || homeType === "state"}
                  typeRequired
                  defaultInput={initOriginInput}
                  defaultSelected={initOriginLocation}
                  defaultType={initOriginType}
                  updateSelectedCity={setOriginLocation}
                  updateType={setOriginType}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <PrimaryTextField
                label="Radius"
                value={originRadius}
                onChange={setOriginRadius}
                maxLength="4"
                isInteger
                fullWidth
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={1}>
              <Box sx={{ display: "flex", justifyContent: "center" }} >
                <InlineIconButton
                  onClick={handleSwitch}
                  size="small"
                  icon={CompareArrowsIcon}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <CityAutocomplete
                  label="Destination"
                  allowState
                  stateOnly={originType === "state" || homeType === "state"}
                  typeRequired
                  defaultInput={initDestInput}
                  defaultSelected={initDestLocation}
                  defaultType={initDestType}
                  updateSelectedCity={setDestLocation}
                  updateType={setDestType}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <PrimaryTextField
                label="Radius"
                value={destRadius}
                onChange={setDestRadius}
                maxLength="4"
                isInteger
                fullWidth
                disabled={isLoading}
              />
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ marginY: "10px" }} />
        <Box>
          <Box sx={{ marginBottom: "10px" }}><Typography variant="label1" color="black_opacity.80">Carrier</Typography></Box>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <CarrierAutocomplete
                label="Carrier Name"
                carrierId={carrierId}
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2}>
              <CarrierDotNumberAutocomplete
                label="DOT #"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2}>
              <CarrierMcNumberAutocomplete
                label="MC #"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2}>
              <CarrierIdAutocomplete
                label="Carrier Code"
                selectedOption={carrier}
                updateSelectedCarrier={setCarrier}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <CityAutocomplete
                  label="Home Location"
                  allowState
                  stateOnly={originType === "state" || destType === "state"}
                  typeRequired
                  defaultInput={initHomeInput}
                  defaultSelected={initHomeLocation}
                  defaultType={initHomeType}
                  updateSelectedCity={setHomeLocation}
                  updateType={setHomeType}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <PrimaryTextField
                label="Radius"
                value={homeLocationRadius}
                onChange={setHomeLocationRadius}
                maxLength="4"
                isInteger
                fullWidth
                disabled={isLoading || !homeLocation?.id}
              />
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Mode"
                  options={modesOptions}
                  value={modes}
                  onChange={setModes}
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Equipment Types"
                  options={equipmentTypesOptions}
                  value={equipmentTypes}
                  onChange={setEquipmentTypes}
                  nameKey="id"
                  valueKey="id"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Brokerage Status"
                  options={loadStatuses}
                  value={brokerageStatuses}
                  onChange={setBrokerageStatuses}
                  nameKey="id"
                  valueKey="id"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Certification"
                  options={certificationOptions}
                  value={certifications}
                  onChange={setCertifications}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Trailer Accessories"
                  options={trailerAccessoriesOptions}
                  value={trailerAccessories}
                  onChange={setTrailerAccessories}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={2}>
              <PrimaryAutocompleteSelect
                label="Dispatchers"
                options={dispatchersOptions}
                value={dispatchers}
                onChange={setDispatchers}
                nameKey="name"
                valueKey="dispatcher_User_Id"
                multiple
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Carrier Status"
                  options={statusOptions}
                  value={carrierStatuses}
                  onChange={setCarrierStatuses}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Pipeline Status"
                  options={pipelineStatusOptions}
                  value={pipelineStatuses}
                  onChange={setPipelineStatuses}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Safety Rating"
                  options={safetyRatingsOptions}
                  value={safetyRatings}
                  onChange={setSafetyRatings}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <PrimaryAutocompleteSelect
                label="Carrier Manager"
                options={managersOptions}
                value={managers}
                onChange={setManagers}
                nameKey="name"
                valueKey="carrier_Mgr"
                multiple
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Cargo Types"
                  options={cargoTypesOptions}
                  value={cargoTypes}
                  onChange={setCargoTypes}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <CustomerAutocomplete
                label="Customers"
                selectedOption={customers}
                updateSelectedCustomer={setCustomers}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Cargo Insurance"
                  options={cargoInsuranceOptions}
                  value={cargoInsurances}
                  onChange={setCargoInsurances}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Truck Units"
                  options={truckUnitsOptions}
                  value={truckUnits}
                  onChange={setTruckUnits}
                  nameKey="name"
                  valueKey="value"
                  allOption
                  multiple
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ display: "flex" }}>
                <PrimarySelect
                  label="Time Frame"
                  options={lookBackDayOptions.map((o) => (o))}
                  onChange={setLookBackDays}
                  value={lookBackDays}
                  nameKey="name"
                  valueKey="value"
                  flexGrow={1}
                  disabled={isLoading}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ display: "flex", marginTop: "10px" }}>
          <Box sx={{ marginRight: "10px" }}>
            <PrimaryButton
              label="Search"
              onClick={handleSearch}
              isLoading={isLoading}
              size="small"
            />
          </Box>
          <Box sx={{ marginRight: "10px" }}>
            <CarrierSearchCreateModal
              handleSave={handleSave}
              isLoading={isLoading}
            />
          </Box>
          <SecondaryButton
            label="Save"
            onClick={handleUpdate}
            isLoading={isLoading}
            size="small"
            disabled={!selectedSearch}
          />
        </Box>
      </Box>
    );
  }, [
    isLoading,
    filters,
    setFilters,
    selectedSearch,
    handleSave,
    handleSearch,
    handleUpdate
  ]);
}

CarrierSearchFilters.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  filters: PropTypes.array.isRequired,
  setFilters: PropTypes.func.isRequired,
  selectedSearch: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  handleSave: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func.isRequired
};